import Cookies from 'js-cookie'
import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import { FineUploaderBasic as Uploader } from 'fine-uploader'

import lang from '@/lang'
import accountConfig from '@/modules/account/config'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'
import letterConfig from '@/modules/letter/config'
import { toast } from '@/modules/base/components/toast/toastConfig'
import CelloService from '@/modules/cello/services/cello.service'
import GtmService, { GtmEventTypes } from '@/modules/_services/gtm.service'

const preserveShopReturnAfterLogin = () => {
  const returnUrl = WebStorageService.getItem(WebStorageKey.RETURN_AFTER_LOGIN)
  if (typeof returnUrl === 'string' && returnUrl.startsWith('/shop')) {
    return returnUrl
  }
  return null
}

const restoreShopReturnAfterLogin = (returnUrl) => {
  if (returnUrl) {
    WebStorageService.setItem(WebStorageKey.RETURN_AFTER_LOGIN, returnUrl)
  }
}

class RegisterService {
  registerMeinbuero({
    token,
    shopToken,
    ga,
    redirectUrl,
  }) {
    return request(accountConfig.endpoints.createTenant, {
      method: 'POST',
      data: { shopToken, ga, createDemoTenant: false, redirectUrl },
      timeout: 600000,
      headers: { authorization: `Bearer ${token}` },
    })
  }

  onRegisterMeinbueroSuccess({ body }) {
    const { data } = body

    const shopReturnUrl = preserveShopReturnAfterLogin()
    WebStorageService.clear()
    restoreShopReturnAfterLogin(shopReturnUrl)

    if (ENV.product === 'meinbuero') {
      GtmService.pushEvent({
        'event': GtmEventTypes.MboDownload,
        'mbodownload-step': 'mbodownload-final',
        'PageURL': '/mbodownload/final',
      })
    }
    return user.login({ data })
  }

  async registerOrgamax({
    email,
    password,
    newsletter,
    shopToken,
    utmMedium,
    utmSource,
    redirectUrl,
  }) {
    let trackingData = Cookies.get('deltra_user')
    try {
      trackingData = JSON.parse(trackingData)
    } catch (e) {
      // swallow
    }

    const registrationData = {
      email: email && email.toLowerCase(),
      password,
      newsletter,
      isDemo: false,
      trackingData,
      createDemoTenant: false,
    }

    if (shopToken) {
      registrationData.shopToken = shopToken
    }
    if (utmMedium) {
      registrationData.utmMedium = utmMedium
    }
    if (utmSource) {
      registrationData.utmSource = utmSource
    }

    // if he has a referralCode add it and the main subscription to the register request
    const referralCode = await CelloService.getUcc()
    if (referralCode) {
      registrationData.referralCode = referralCode
      registrationData.subscriptionTypeTypes = ['main']
    }

    const response = await request(accountConfig.endpoints.registration, {
      method: 'POST',
      data: registrationData,
      timeout: 60000,
    })
    return Promise.all([
      { redirectUrl, ...response.body.data },
      password ? request(accountConfig.endpoints.login, { method: 'POST', data: { email, password } }) : null,
    ])
  }

  onRegisterOrgamaxSuccess([userData, loginResponse]) {
    const data = Object.assign({}, {
      id: userData.id,
      email: userData.email,
      redirectUrl: userData.redirectUrl,
      token: userData.token,
      registeredAt: userData.registeredAt,
      subscription: userData.subscription,
    }, loginResponse ? loginResponse.body.data : {})

    const shopReturnUrl = preserveShopReturnAfterLogin()
    WebStorageService.clear()
    restoreShopReturnAfterLogin(shopReturnUrl)

    toast.showToast({ message: lang.registrationSuccess })

    // conversion event for gtm
    if (ENV.product === 'orgamax') {
      GtmService.pushEvent({ event: GtmEventTypes.RegistrationRequested })
    }

    return user.login({ data })
  }

  onRegisterError(e) {
    if (e?.body?.meta?.email?.some(email => email.code === 'NOT_ALLOWED')) {
      toast.showToast({ type: 'error', message: lang.registerError })
    } else {
      toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
    }
  }

  async finishOnboarding({
    password,
    acceptAvv,
    companySize,
    isSmallBusiness,
    companyData,
    industry,
    isSubjectToImputedTaxation,
    logo: { x, y, sortId, section, file, metaData } = {},
  }) {
    const onboardingData = {
      password,
      acceptAvv,
      companySize,
      isSmallBusiness,
      companyData,
      industry,
      isSubjectToImputedTaxation,
      logoData: { x, y, sortId, section, metaData },
    }

    if (file) {
      return new Promise((resolve, reject) => {
        const uploader = new Uploader(
          Object.assign({}, letterConfig.fineUploader, {
            autoUpload: false,
            multiple: false,
            callbacks: {
              onError: (id, name, reason, xhr) => reject(reason),
              onComplete: (id, name, response, xhr) => resolve({ body: response }),
            },
            request: {
              endpoint: accountConfig.endpoints.finishOnboarding,
              customHeaders: { Authorization: `Bearer ${user.token}` },
              inputName: 'logo',
            },
          }),
        )

        uploader.setParams(
          onboardingData,
        )
        uploader.addBlobs([{ name: 'logo', blob: file }])
        uploader.uploadStoredFiles()
      })
    } else {
      return request(accountConfig.endpoints.finishOnboarding, {
        method: 'POST',
        data: onboardingData,
        auth: true,
      })
    }
  }

  async register(data = {}, cb) {
    let result = null
    try {
      if (ENV.product === 'orgamax') {
        result = await this.registerOrgamax(data)
        await this.onRegisterOrgamaxSuccess(result)
      } else {
        result = await this.registerMeinbuero(data)
        await this.onRegisterMeinbueroSuccess(result)
      }
    } catch (e) {
      this.onRegisterError(e)
      cb?.()
    }
  }
}

export default new RegisterService()
